import { logger } from "@/utils/logger";
import React, { createContext, useContext, useEffect, useState } from 'react';
import { getAppSettings, updateAppSettings } from '@/utils/services/settingsService';
import { supabase } from '@/integrations/supabase/client';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: React.Dispatch<React.SetStateAction<Theme>>;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Create a debounced function for theme updates to prevent excessive DB calls
const createDebouncedThemeUpdate = () => {
  let timeout: number | null = null;
  let lastTheme: Theme | null = null;
  
  return (theme: Theme) => {
    // Store the latest theme value
    lastTheme = theme;
    
    // If there's a pending update, cancel it
    if (timeout) {
      window.clearTimeout(timeout);
    }
    
    // Schedule a new update
    timeout = window.setTimeout(() => {
      // Only update if we have authentication
      if (lastTheme) {
        updateAppSettings({ themePreference: lastTheme }).catch(error => {
          logger.warn('Could not save theme to settings:', error);
          // Still have localStorage as fallback, so this is not critical
        });
      }
      timeout = null;
    }, 1000); // Debounce for 1 second
  };
};

// Function to determine initial theme from localStorage or system preference
const getThemeFromLocalOrSystem = (): Theme => {
  if (typeof window !== 'undefined') {
    const localTheme = localStorage.getItem('theme') as Theme | null;
    if (localTheme) {
      return localTheme;
    }
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
  }
  return 'light'; // Default fallback
};

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  // Initialize theme directly from local/system preference
  const [theme, setTheme] = useState<Theme>(getThemeFromLocalOrSystem()); 
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const debouncedThemeUpdate = React.useRef(createDebouncedThemeUpdate()).current;

  // Main effect for initial theme loading and authentication check
  useEffect(() => {
    const loadAndSyncTheme = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        const authenticated = !!data.session;
        setIsAuthenticated(authenticated);

        if (authenticated) {
          try {
            const settings = await getAppSettings(true); // Force refresh from DB
            if (settings.themePreference) {
              setTheme(settings.themePreference as Theme);
              localStorage.setItem('theme', settings.themePreference);
            }
          } catch (error) {
            logger.error('Error loading theme from settings after auth:', error);
          }
        }
      } catch (error) {
        logger.error('Error checking auth status:', error);
        setIsAuthenticated(false);
      }
    };

    loadAndSyncTheme();
    
    // Listen for auth state changes
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      const authenticated = !!session;
      setIsAuthenticated(authenticated);
      if (authenticated) {
        getAppSettings(true).then(settings => {
          if (settings.themePreference) {
            setTheme(settings.themePreference as Theme);
            localStorage.setItem('theme', settings.themePreference);
          }
        }).catch(error => {
          logger.error('Error loading theme on auth state change:', error);
        });
      } else {
        const initialTheme = getThemeFromLocalOrSystem();
        setTheme(initialTheme);
        localStorage.setItem('theme', initialTheme);
      }
    });
    
    return () => {
      authListener?.subscription.unsubscribe();
    };
  }, []); // Empty dependency array to run only once on mount

  // Apply theme class to document
  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
    // localStorage is already updated in loadAndSyncTheme and toggleTheme
  }, [theme]); // Only depend on 'theme'

  // Toggle between light and dark
  const toggleTheme = () => {
    setTheme(prev => {
      const newTheme = prev === 'light' ? 'dark' : 'light';
      // Update localStorage immediately
      localStorage.setItem('theme', newTheme); 
      if (isAuthenticated) {
        // Update database immediately without debounce on user interaction
        updateAppSettings({ themePreference: newTheme }).catch(error => {
          logger.warn('Could not save theme to settings:', error);
        });
      }
      return newTheme;
    });
  };

  // No longer need to return null for loading state, as theme is always initialized
  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
